import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { authFetch } from "../lib/auth";
import Requirements, { type ContractType } from "../components/sections/Requirements";
import { useAuth } from "../context/AuthProvider";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const VAT_NOTICE_VERSION = "2026-05-08.v1";
const VAT_NOTICE_TITLE = "카드결제 부가세 이중 환급 금지 안내";
const VAT_NOTICE_TEXT = [
  "본 카드결제분에 대한 부가세는 당사에서 별도 처리하지 않습니다.",
  "거래 상대방으로부터 받은 세금계산서(현금영수증) 기준으로만 환급 신청하시기 바랍니다.",
  "이중 환급 시 세무조사 대상이 될 수 있습니다.",
];
const VAT_NOTICE_TARGETS = new Set(["goods", "maintenance", "rent", "rental", "drink"]);

function vatNoticeCategoryKey(category: string | ContractType | undefined) {
  const v = String(category || "").trim().toLowerCase();
  if (v === "fee" || v === "mgmt_fee") return "maintenance";
  return v || "drink";
}

function vatNoticeStorageKey(sessionKey: string, category: string | ContractType) {
  return `julyupay:vatNotice:${sessionKey}:${vatNoticeCategoryKey(category)}:${VAT_NOTICE_VERSION}`;
}

function getKstDateKey(date = new Date()) {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Seoul" }).format(date);
}

type MeProfile = {
  id: number;
  email: string;
  full_name: string | null;
  phone: string | null;
  name?: string;
  ekyc_status?: string | null;
};

function normalizeCategory(_value?: string): ContractType {
  // 주류/음료 단일 유형으로 고정
  return "drink";
}

function StepCard({
  step,
  title,
  totalSteps,
  onBack,
  description,
  children,
}: {
  step: number;
  title: string;
  totalSteps: number;
  onBack: () => void;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-[22px] bg-white">
      <div className="border-b border-slate-100 px-3 py-3 sm:px-8 sm:py-6">
        <div className="mb-3 grid grid-cols-[72px_1fr_72px] items-center gap-2 sm:mb-4 sm:grid-cols-[88px_1fr_88px]">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:px-3.5 sm:py-2 sm:text-sm"
          >
            <span aria-hidden="true">←</span>
            <span>이전</span>
          </button>
          <div className="text-center">
            <div className="text-[17px] font-bold leading-tight text-slate-900 sm:text-[20px]">{title}</div>
          </div>
          <div className="text-right text-xs font-semibold text-slate-400 sm:text-sm">
            {step}/{totalSteps}
          </div>
        </div>

        {description ? (
          <p className="text-sm leading-relaxed text-slate-600">{description}</p>
        ) : null}
      </div>

      <div className="px-3 py-3 sm:px-8 sm:py-7">{children}</div>
    </section>
  );
}

export default function ContractStart() {
  const nav = useNavigate();
  const { user } = useAuth();
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const skipIdentityVerify = !!user?.is_admin;

  const editingId = params.id ? Number(params.id) : null;
  void editingId;

  const categoryFromQuery = normalizeCategory(sp.get("category") || sp.get("type") || "drink");
  const sessionFromQuery = sp.get("session_key") || undefined;

  const [sessionKey] = useState<string>(() => {
    if (sessionFromQuery) return sessionFromQuery;

    const randomValue =
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);

    return randomValue;
  });

  const [sessionVerified, setSessionVerified] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [service, setService] = useState<ContractType>(categoryFromQuery);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [docsOk, setDocsOk] = useState(false);
  const [targetStep, setTargetStep] = useState<number | null>(null);
  const [vatNoticeOpen, setVatNoticeOpen] = useState(false);
  const [vatNoticeChecked, setVatNoticeChecked] = useState(false);
  const [vatNoticeSaving, setVatNoticeSaving] = useState(false);
  const currentStepRef = useRef(1);

  const todayKey = getKstDateKey();
  const extraNoteKey = `julyupay:extraNote:${sessionKey}`;
  const ekycKey = `julyupay:session_ekyc:${sessionKey}`;
  const dailyEkycSharedKey = `julyupay:daily_ekyc:shared:${todayKey}`;
  const dailyEkycUserKey = userId ? `julyupay:daily_ekyc:user:${userId}:${todayKey}` : null;

  const markTodayVerified = (targetUserId?: number | null) => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(ekycKey, "ok");
      window.localStorage.setItem(dailyEkycSharedKey, todayKey);
      const resolvedUserId = Number.isFinite(Number(targetUserId)) ? Number(targetUserId) : userId;
      if (resolvedUserId) {
        window.localStorage.setItem(`julyupay:daily_ekyc:user:${resolvedUserId}:${todayKey}`, todayKey);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const raw = window.localStorage.getItem(extraNoteKey);
      if (raw != null) setExtraNote(raw);
    } catch {
      /* ignore */
    }
  }, [extraNoteKey]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(extraNoteKey, extraNote);
    } catch {
      /* ignore */
    }
  }, [extraNote, extraNoteKey]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const sessionRaw = window.localStorage.getItem(ekycKey);
      const sharedRaw = window.localStorage.getItem(dailyEkycSharedKey);
      const userRaw = dailyEkycUserKey ? window.localStorage.getItem(dailyEkycUserKey) : null;
      setSessionVerified(sessionRaw === "ok" || sharedRaw === todayKey || userRaw === todayKey);
    } catch {
      /* ignore */
    }
  }, [dailyEkycSharedKey, dailyEkycUserKey, ekycKey, todayKey]);

  useEffect(() => {
    if (sp.get("session_ekyc") !== "ok") return;
    setSessionVerified(true);
    markTodayVerified();
    setTargetStep(null);

    const next = new URLSearchParams(sp);
    next.delete("session_ekyc");
    setSp(next, { replace: true });
  }, [setSp, sp]);

  useEffect(() => {
    (async () => {
      try {
        const profileRes = await authFetch(`${API_BASE}/api/me/profile`, { method: "GET" });
        const profileJson = await profileRes.json().catch(() => null);

        let profile: MeProfile | null = null;
        if (profileRes.ok && profileJson?.ok && profileJson?.user) {
          profile = profileJson.user as MeProfile;
        }

        if (!profile?.full_name || !profile?.ekyc_status) {
          const meRes = await authFetch(`${API_BASE}/api/me`, { method: "GET" });
          const meJson = await meRes.json().catch(() => null);
          if (meRes.ok && meJson?.ok && meJson?.user) {
            profile = {
              ...(profile || (profileJson?.user as MeProfile) || {}),
              name: meJson.user.name,
              ekyc_status: meJson.user.ekyc_status ?? profile?.ekyc_status ?? null,
            } as MeProfile;
          }
        }

        if (!profile) return;

        const numericUserId = Number.isFinite(Number(profile.id)) ? Number(profile.id) : null;
        setUserId(numericUserId);
        setFullName(profile.full_name || profile.name || "");
        setPhone(profile.phone || "");

        try {
          const ekycRes = await authFetch(`${API_BASE}/api/ekyc/status`, { method: "GET" });
          const ekycJson = await ekycRes.json().catch(() => null);
          const serverVerified =
            ekycJson?.status === "verified" || profile?.ekyc_status === "verified";
          if (serverVerified) {
            setSessionVerified(true);
            markTodayVerified(numericUserId);
          }
        } catch {
          if (profile?.ekyc_status === "verified") {
            setSessionVerified(true);
            markTodayVerified(numericUserId);
          }
        }
      } catch {
        /* noop */
      }
    })();
  }, [dailyEkycSharedKey, ekycKey, todayKey, userId]);

  const nextPath = useMemo(
    () =>
      `/contracts/new/transfer?category=${encodeURIComponent(service)}&session_key=${encodeURIComponent(
        sessionKey
      )}`,
    [service, sessionKey]
  );

  const reverifyReturnTo = useMemo(
    () =>
      `/contracts/new?category=${encodeURIComponent(service)}&session_key=${encodeURIComponent(
        sessionKey
      )}`,
    [service, sessionKey]
  );

  const totalSteps = skipIdentityVerify ? 2 : 3;
  const calculatedStep = !docsOk ? 1 : skipIdentityVerify || sessionVerified ? 3 : 2;
  const currentStep = targetStep ?? calculatedStep;
  const displayStep = skipIdentityVerify && currentStep === 3 ? 2 : currentStep;
  const canMemoNext = docsOk && (skipIdentityVerify || sessionVerified);
  const vatCategory = vatNoticeCategoryKey(service);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const hasVatNoticeAgreement = (category: string | ContractType = service) => {
    const keyCategory = vatNoticeCategoryKey(category);
    if (!VAT_NOTICE_TARGETS.has(keyCategory)) return true;
    try {
      return (
        typeof window !== "undefined" &&
        window.sessionStorage.getItem(vatNoticeStorageKey(sessionKey, keyCategory)) === "1"
      );
    } catch {
      return false;
    }
  };

  const requireVatNoticeAgreement = (category: string | ContractType = service) => {
    const keyCategory = vatNoticeCategoryKey(category);
    if (hasVatNoticeAgreement(keyCategory)) return true;
    setVatNoticeChecked(false);
    setVatNoticeOpen(true);
    return false;
  };

  useEffect(() => {
    if (!VAT_NOTICE_TARGETS.has(vatCategory)) {
      setVatNoticeOpen(false);
      return;
    }
    if (hasVatNoticeAgreement(vatCategory)) {
      setVatNoticeOpen(false);
      return;
    }
    setVatNoticeChecked(false);
    setVatNoticeOpen(true);
  }, [sessionKey, vatCategory]);

  const agreeVatNotice = async () => {
    if (!vatNoticeChecked || vatNoticeSaving) return;
    setVatNoticeSaving(true);
    try {
      const resp = await authFetch("/api/agreements/vat-double-refund", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: vatCategory,
          session_key: sessionKey,
          notice_version: VAT_NOTICE_VERSION,
          agreed: true,
        }),
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json?.ok) {
        throw new Error(json?.message || "동의 로그 저장에 실패했습니다.");
      }
      try {
        window.sessionStorage.setItem(vatNoticeStorageKey(sessionKey, vatCategory), "1");
      } catch {
        /* ignore */
      }
      setVatNoticeOpen(false);
    } catch (e: any) {
      alert(e?.message || "동의 처리 중 오류가 발생했습니다.");
    } finally {
      setVatNoticeSaving(false);
    }
  };

  const handleServiceChange = (raw: string) => {
    const next = normalizeCategory(raw);
    setDocsOk(false);
    setTargetStep(1);
    setService(next);
    setVatNoticeChecked(false);
    const nextCategory = vatNoticeCategoryKey(next);
    if (VAT_NOTICE_TARGETS.has(nextCategory) && !hasVatNoticeAgreement(nextCategory)) {
      setVatNoticeOpen(true);
    } else {
      setVatNoticeOpen(false);
    }
  };

  const handleStepBack = (step: number) => {
    if (step === 3) {
      setTargetStep(skipIdentityVerify ? 1 : 2);
      return;
    }

    if (step === 2) {
      setTargetStep(1);
      return;
    }

    nav("/", { replace: true });
  };

  useEffect(() => {
    const guardStateKey = "__julyupay_contract_back_guard__";

    const pushGuardState = () => {
      const currentState =
        window.history.state && typeof window.history.state === "object"
          ? window.history.state
          : {};
      window.history.pushState({ ...currentState, [guardStateKey]: true }, "", window.location.href);
    };

    pushGuardState();

    const onPopState = () => {
      const stepNow = currentStepRef.current;
      if (stepNow > 1) {
        handleStepBack(stepNow);
        pushGuardState();
        return;
      }

      nav("/", { replace: true });
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [nav]);

  return (
    <div className="kp-mobile-fit min-h-screen w-full bg-white px-0 py-0 md:px-6 md:py-4">
      <div className="mx-auto w-full max-w-6xl px-3 py-3 sm:px-8 sm:py-9">
        <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-slate-100 sm:mb-4">
          <div
            className="h-full bg-gradient-to-r from-[#ff8a1f] to-[#f97316] transition-all duration-300"
            style={{ width: `${(displayStep / totalSteps) * 100}%` }}
          />
        </div>

        <div className="space-y-4">
          {currentStep === 1 ? (
            <StepCard
              step={1}
              title="서류 준비"
              totalSteps={totalSteps}
              onBack={() => handleStepBack(1)}
            >
              <div>
                <Requirements
                  type={service}
                  className="mt-0"
                  sessionKey={sessionKey}
                  onSatisfiedChange={(ok) => setDocsOk(ok)}
                />

                {docsOk ? (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (!requireVatNoticeAgreement(service)) return;
                        setTargetStep(skipIdentityVerify ? 3 : 2);
                      }}
                      className="inline-flex w-full items-center justify-center rounded-[18px] bg-[#f97316] px-5 py-3 text-sm font-bold text-white shadow-[0_14px_28px_rgba(224,114,30,0.22)] transition hover:brightness-105 active:translate-y-px sm:w-auto"
                    >
                      다음
                    </button>
                  </div>
                ) : null}
              </div>
            </StepCard>
          ) : null}

          {!skipIdentityVerify && currentStep === 2 ? (
            <StepCard
              step={2}
              title="신청인 정보 및 신분증 재인증"
              totalSteps={totalSteps}
              onBack={() => handleStepBack(2)}
            >
              <div className="space-y-4">
                <div className="rounded-[16px] bg-white p-4 sm:p-5">
                  <div className="mb-4 text-base font-extrabold text-slate-900">신청인 본인 정보</div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                    <div>
                      <div className="mb-1.5 text-sm font-semibold text-slate-700">성함</div>
                      <input
                        className="w-full rounded-[14px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
                        value={fullName}
                        readOnly
                      />
                    </div>

                    <div>
                      <div className="mb-1.5 text-sm font-semibold text-slate-700">연락처</div>
                      <input
                        className="w-full rounded-[14px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
                        value={phone}
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-[16px] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.05)] sm:p-5">
                  <div className="mb-2 text-base font-extrabold text-slate-900">신분증 재인증</div>
                  {sessionVerified ? (
                    <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                      오늘 신분증 재인증이 완료되었습니다.
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => nav(`/ekyc/reverify?returnTo=${encodeURIComponent(reverifyReturnTo)}`)}
                      className="inline-flex w-full items-center justify-center rounded-[18px] bg-[#f97316] px-5 py-3 text-sm font-bold text-white shadow-[0_14px_28px_rgba(224,114,30,0.22)] transition hover:brightness-105 active:translate-y-px sm:w-auto"
                    >
                      신분증 재인증 하러가기
                    </button>
                  )}
                </div>

                {sessionVerified ? (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (!requireVatNoticeAgreement(service)) return;
                        setTargetStep(3);
                      }}
                      className="inline-flex w-full items-center justify-center rounded-[18px] bg-[#f97316] px-5 py-3 text-sm font-bold text-white shadow-[0_14px_28px_rgba(224,114,30,0.22)] transition hover:brightness-105 active:translate-y-px sm:w-auto"
                    >
                      다음
                    </button>
                  </div>
                ) : null}
              </div>
            </StepCard>
          ) : null}

          {currentStep === 3 ? (
            <StepCard
              step={displayStep}
              title="기타내용 입력"
              totalSteps={totalSteps}
              onBack={() => handleStepBack(3)}
            >
              <div>
                  <textarea
                  className="min-h-[180px] w-full rounded-[14px] border border-slate-200 bg-white px-4 py-4 text-sm leading-6 text-slate-900 outline-none focus:border-[#f97316] focus:ring-4 focus:ring-[#f97316]/10"
                  placeholder="특이사항이나 기타내용이 있으면 작성하세요."
                  maxLength={500}
                  value={extraNote}
                  onChange={(e) => setExtraNote(e.target.value)}
                />

                <div className="mt-4 flex items-center justify-end text-xs text-slate-500">
                  <span>{extraNote.length}/500</span>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (!requireVatNoticeAgreement(service)) return;
                      nav(nextPath);
                    }}
                    disabled={!canMemoNext}
                    className="inline-flex w-full items-center justify-center rounded-[14px] bg-[#f97316] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(249,115,22,0.22)] transition hover:bg-[#ea580c] active:translate-y-px disabled:cursor-not-allowed disabled:bg-[#fed7aa] sm:w-auto"
                  >
                    다음
                  </button>
                </div>
              </div>
            </StepCard>
          ) : null}
        </div>
      </div>
      {vatNoticeOpen ? (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-xl rounded-[22px] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.24)] sm:p-6">
            <h2 className="text-lg font-extrabold tracking-tight text-slate-950 sm:text-xl">
              {VAT_NOTICE_TITLE}
            </h2>
            <div className="mt-5 space-y-3 text-sm leading-6 text-slate-700 sm:text-base">
              <p>{VAT_NOTICE_TEXT[0]}</p>
              <p>{VAT_NOTICE_TEXT[1]}</p>
              <p className="font-extrabold text-rose-600">{VAT_NOTICE_TEXT[2]}</p>
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={vatNoticeChecked}
                onChange={(e) => setVatNoticeChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#f97316] focus:ring-[#f97316]"
              />
              <span>위 내용을 확인했으며, 부가세 이중 환급 금지 안내에 동의합니다.</span>
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => nav("/", { replace: true })}
                className="rounded-[14px] border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={agreeVatNotice}
                disabled={!vatNoticeChecked || vatNoticeSaving}
                className="rounded-[14px] bg-[#f97316] px-4 py-2 text-sm font-bold text-white hover:bg-[#ea580c] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {vatNoticeSaving ? "저장 중..." : "동의하고 진행"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
